import { useForm, FormProvider } from 'react-hook-form';
import TextAreaInputForm from '@/components/forms/TextAreaInputForm';
import TextInputForm from '@/components/forms/TextInputForm';
import UnderlinePlusHeading from '@/components/heading/UnderlinePlusHeading';
import { useMemo, useState } from 'react';
import {
    AddMessageFormValues,
    AddMessageSchema,
} from '@/features/services/components/forms/schemas/add-message-schema';
import { apiPost } from '@/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { zodResolver } from '@hookform/resolvers/zod';
import Submission from './Submission';
import PlusIcon from '@/components/icons/popups/PlusIcon';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

interface ServiceRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ServiceRequestModal({ isOpen, onClose }: ServiceRequestModalProps) {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    useBodyScrollLock(true);

    const defaultValues = useMemo(
        () => ({
            customer_id: '',
            name_ar: '', // Added to match form fields
            name_en: '',
            disc_ar: '',
            disc_en: '',
            notes: '',
            price: '',
        }),
        [],
    );

    const form = useForm<AddMessageFormValues>({
        resolver: zodResolver(AddMessageSchema),
        defaultValues,
    });

    const onSubmit = async (data: AddMessageFormValues) => {
        setError(null);
        setLoading(true);
        try {
            await apiPost({
                endpoint: endpoints.services.create,
                body: data,
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });
            setShowSuccessPopup(true);
        } catch (error: any) {
            if (error.response?.data?.errors) {
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    form.setError(field as any, {
                        type: 'manual',
                        message: (messages as string[]).join(', '),
                    });
                });
            } else {
                let errorMessage = 'حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى.';
                if (error.response) {
                    errorMessage = error.response.data.msg || errorMessage;
                } else if (error.request) {
                    errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
                }
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative bg-white-blue-100 rounded-[12px] md:p-8 p-6 w-full md:max-w-xl max-w-md shadow-lg mx-3">
                <FormProvider {...form}>
                    <UnderlinePlusHeading text="اضافة خدمة جديدة" onClick={onClose} icon={PlusIcon} />
                    <div className="flex flex-col md:gap-4 sm:gap-[14px] gap-3">
                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <TextInputForm
                                className="w-full"
                                name="name_ar"
                                label="اسم الخدمة بالعربية"
                                placeholder="ادخل اسم القسم بالعربي"
                            />
                            <TextInputForm
                                className="w-full"
                                name="name_en"
                                label="اسم الخدمة بالانجليزية"
                                placeholder="ادخل اسم القسم بالانجليزي"
                            />
                        </div>
                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <TextInputForm
                                className="w-full"
                                name="disc_ar"
                                label="وصف مختصر للخدمة بالعربية"
                                placeholder=" وصف مختصر بالعربي"
                            />
                            <TextInputForm
                                className="w-full"
                                name="disc_en"
                                label="وصف مختصر للخدمة بالانجليزية"
                                placeholder="وصف مختصر بالانجليزي"
                            />
                        </div>
                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <TextInputForm
                                name="price"
                                label="السعر"
                                placeholder="ادخل السعر"
                                className="w-full"
                            />
                        </div>
                        <div className="mb-6">
                            <TextAreaInputForm
                                name="notes"
                                label="ملاحظات"
                                placeholder="ملاحظات بسيطة"
                                className="!h-32"
                            />
                        </div>
                    </div>
                    {/* <div className="mt-6 flex justify-between gap-4">
                        <button
                            type="button"
                            onClick={() => setShowCancelPopup(true)}
                            className="py-2 text-[16px] font-medium text-red-800 bg-red-200 rounded-md hover:bg-red-300 w-full"
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            className="py-2 text-[16px] font-medium text-white-blue-50 bg-primery-blue-500 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-green-500"
                            disabled={loading}
                        >
                            {loading ? 'جاري التحميل...' : 'حفظ الطلب'}
                        </button>
                    </div> */}
                    {error && <p className="text-red-600 mt-4">{error}</p>}
                    <Submission
                        // maintenanceOrder={null}
                        loading={loading}
                        setShowCancelPopup={setShowCancelPopup}
                        setShowSuccessPopup={setShowSuccessPopup}
                        showCancelPopup={showCancelPopup}
                        showSuccessPopup={showSuccessPopup}
                        onClose={onClose}
                        onAddAnother={() => {
                            form.reset(); // مسح البيانات
                            setShowSuccessPopup(false); // إغلاق البوب أب
                        }}
                        onSubmit={form.handleSubmit(onSubmit)}
                    />
                </FormProvider>
            </div>
        </div>
    );
}
